import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { VenueDetailComponent } from "./venue-detail/venue-detail.component";
import { EventFormComponent } from "./event-form/event-form.component";
import { ValidationMessageComponent } from "../../../../shared/components/validation-message/validation-message.component";
import { InlineCalendarComponent } from "../../../../shared/components/inline-calendar/inline-calendar.component";
import { ServiceProgressComponent } from "../../../../shared/components/service-progress/service-progress.component";
import { SkeletonLoaderComponent } from "../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { ScreenLoaderComponent } from '../../../../shared/components/screen-loader/screen-loader.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesPath } from '../../../../shared/constants/routes-path';
import { VenueData } from './venus.data';
import { ApiService } from '../../../../core/services/api.service';
import { ApiEndPoints, ApiRoot } from '../../../../shared/constants/api-endpoints';
import { SharedService } from '../../../../shared/services/shared.service';
import { Utils } from '../../../../Utils/utils';
import { normalizeIban } from '../../../../Utils/validator';
import { DocumentServiceType, FieldValue, ServiceName, SrActionType } from '../../../../shared/constants/services.enum';
import { DCSRVenueBookingRoomImagesBC, VenueBookingRequest } from '../../../../shared/models/services-request/venue-booking.model';
import { VenueBookingQueryRequest } from '../../../../shared/models/services-request/venue-booking-query.model';
import { SrStatusRequest } from '../../../../shared/models/services-request/sr-status-request.model';
import { AttachmentService } from '../../../../core/services/attachment.service';
import { firstValueFrom, Observable, of, switchMap, throwError } from 'rxjs';
import { SrStatus } from '../../../../shared/constants/status.enum';
import { environment } from '../../../../../environments/environment';
import { VenueTokenService } from '../../../../core/services/venue-token.service';
import { generateCateringProvidersPdfFromHtml } from './catering-provider-pdf.util';
interface TimeSlot {
  label: string;
  booked?: boolean
}
export interface Venue {
  id: number;
  name: string;
  desc: string;
  seats: string;
  accessible: boolean;
  fullDayRate: number;
  hourlyRate: number;
  image: string;
  days: any; // "YYYY-MM-DD"  -> timeslots
  venueBookingId?: number;
}
@Component({
  selector: 'app-venue',
  imports: [CommonModule, TranslateModule, ButtonComponent, EventFormComponent, ValidationMessageComponent, InlineCalendarComponent, ServiceProgressComponent, SkeletonLoaderComponent, ScreenLoaderComponent, VenueDetailComponent],
  templateUrl: './venue.component.html',
  styleUrl: './venue.component.scss'
})
export class VenueComponent implements OnInit, OnDestroy {
  private readonly venueImageHostUrl = environment.venueImageHostUrl;
  formProgress: number = 0;
  step: number = 1;
  confirmedVenue: any = "";
  venues: Venue[] = [];
  roomProfiles: any[] = [];
  roomProfilesLoading = false;
  availabilityCheckLoading = false;
  roomReservationLoading = false;
  showVenueDetailPopup = false;
  selectedVenueDetail: any = null;
  selectedVenue: Venue | null = null;
  selectedDate: any = new Date();
  bookedDates: string[] = [];
  calendarVisibleMonth: Date = new Date();
  selectedSlot: TimeSlot | null = null;
  mode: 'slots' | 'full' = 'slots';
  isDetailPage: boolean = false;
  masterDataLoading = false;
  eventTypeOptions: Array<{ label: string; value: string }> = [];
  roomSetupOptions: Array<{ label: string; value: string }> = [];
  cateringOptionOptions: Array<{ label: string; value: string }> = [];
  cateringTypeOptions: Array<{ label: string; value: string }> = [];
  reminderOptions: Array<{ label: string; value: string }> = [];
  cancellationOptions: Array<{ label: string; value: string }> = [];
  cateringProviderHotels: VenueCateringProviderHotel[] = [];
  srNumber: string = '';
  eventFormInitialData: Record<string, any> | null = null;
  venueSrDetailLoading = false;
  currentSrStatus: string = SrStatus.Draft || 'Draft';
  private pendingVenueSrDetail: any = null;
  private formProgressUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  cateringProviderPdfLoading = false;
  showActions: boolean = true;
  userCSN: string = '';
  get allowVenueActions(): boolean {
    return this.showActions && !this.roomReservationLoading;
  }
  get showReservationHoldMessage(): boolean {
    const hiddenStatuses: string[] = [
      String(SrStatus.Draft)
    ];
    return hiddenStatuses.includes(this.currentSrStatus);
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private api: ApiService,
    private sharedService: SharedService,
    private attachmentService: AttachmentService,
    private venueTokenService: VenueTokenService
  ) {
    this.userCSN = this.sharedService.getCsnAccountId('csn');
    this.roomProfilesLoading = true;
    this.route.params.subscribe((p: any) => {
      if (p.id) {
        this.isDetailPage = true;
      }
      if (p['sr_no']) {
        this.venueSrDetailLoading = true;
        this.srNumber = String(p['sr_no']).trim();
      }
    })
    const routeCsn = this.route.snapshot.params?.['csn'];
    if (routeCsn) {
      this.userCSN = routeCsn;
    }
    if (sessionStorage.getItem('selectedVenue')) {
      let storageVenue = JSON.parse(sessionStorage.getItem('selectedVenue') ?? '{}');
      if (storageVenue?.venue?.venueBookingId) {
        this.selectedVenue = storageVenue.venue;
        this.confirmedVenue = storageVenue;
        this.srNumber = this.srNumber || (sessionStorage.getItem('srNumber') ? sessionStorage.getItem('srNumber') : '') || '';
        this.step = 2
      }
    }
  }

  ngOnInit(): void {
    this.roomProfilesLoading = true;
    this.checkLoginUserInLiferay().pipe(
      switchMap(() => this.venueTokenService.ensureVenueToken())
    ).subscribe({
      next: () => {
        this.loadMasterData();
        this.loadRoomProfiles();
        this.openFolder();
      },
      error: () => {

      }
    });
  }

  // This method is used to check is login user email exist in liferay, if not the create user in liferay system
  private checkLoginUserInLiferay(): Observable<any> {
    const loginUser: any = this.sharedService.getUser();
    // let userProfile = loginUser?.user;
    const userProfile = this.sharedService.getUserProfile() || {};
    // const alternateName = String(userProfile?.['username'] ?? '').trim();
    // const emailAddress = String(userProfile?.['email'] ?? '').trim();
    // const givenName = String(userProfile?.['given_name'] ?? '').trim();
    // const familyName = String(userProfile?.['family_name'] ?? '').trim();

    const alternateName = String(userProfile?.['Login Name'] ?? '').trim();
    const emailAddress = String(userProfile?.['EMail Addr'] ?? '').trim();
    const givenName = String(userProfile?.['First Name'] ?? '').trim();
    const familyName = String(userProfile?.['Last Name'] ?? '').trim();

    if (!alternateName || !emailAddress) {
      return of(null);
    }
    return this.api.checkUserInLiferay(alternateName).pipe(
      switchMap((response: any) => {
        if (response && response?.totalCount == 0) {
          // User does not exist in Liferay, create the user
          return this.api.createUserInLiferay({
            alternateName,
            emailAddress,
            givenName,
            familyName,
            roleExternalReferenceCode: 'ESG User',
            status: 'Active'
          }).pipe(
            switchMap((createResponse: any) => {
              const createStatus = String(createResponse?.status ?? '').trim().toUpperCase();

              if (createStatus === 'BAD_REQUEST') {
                return throwError(() => createResponse);
              }

              return of(createResponse);
            })
          );
        }

        return of(null);
      })
    );
  }
  gotoDetailPage(id: any) {
    this.selectedVenueDetail = this.venues.find((v: Venue) => v.id == id) ?? null;
    this.showVenueDetailPopup = !!this.selectedVenueDetail;
  }

  closeVenueDetailPopup(): void {
    this.showVenueDetailPopup = false;
    this.selectedVenueDetail = null;
  }
  // This method is used to pick venue using v
  pickVenue(v: Venue) {
    this.selectedVenue = v;
    this.selectedDate = null;
    this.selectedSlot = null;
    this.calendarVisibleMonth = new Date();
    this.loadBookedDatesForMonth(this.calendarVisibleMonth);
  }
  // This method is used to pick slot using ts
  pickSlot(ts: TimeSlot) {
    this.selectedSlot = ts;
  }
  changeBooking() {
    const venueBookingId = String(this.selectedVenue?.venueBookingId ?? '').trim();
    if (!venueBookingId) {
      this.proceedWithChangeBooking();
      return;
    }

    this.roomReservationLoading = true;
    this.api.deleteVenueBooking(venueBookingId).subscribe({
      next: () => {
        if (this.selectedVenue) {
          delete this.selectedVenue.venueBookingId;
        }
        if (this.confirmedVenue?.venue) {
          delete this.confirmedVenue.venue.venueBookingId;
        }
        sessionStorage.setItem('selectedVenue', JSON.stringify(this.confirmedVenue));
        this.roomReservationLoading = false;
        this.proceedWithChangeBooking();
      },
      error: () => {
        this.roomReservationLoading = false;
      }
    });
  }

  private proceedWithChangeBooking() {
    this.step = 1;
    this.formProgress = 20;
    // this.selectedDate = null;
    // this.selectedSlot = null;
    // this.calendarVisibleMonth = new Date();
    this.loadBookedDatesForMonth(this.calendarVisibleMonth);
  }
  // This method is used to confirm
  confirm() {
    if (!this.selectedVenue || !this.selectedDate) {
      this.sharedService.toastMessage('Error', 'SELECT_DATE', 'error');
      return;
    }

    const roomProfileId = this.getRoomProfileId(this.selectedVenue);
    if (!roomProfileId) {
      this.sharedService.toastMessage('Error', 'Room profile id is missing', 'error');
      return;
    }
    if (sessionStorage.getItem('selectedVenue')) {
      sessionStorage.removeItem('selectedVenue');
      sessionStorage.removeItem('srNumber');
    }

    const { startTime, endTime } = this.getAvailabilityRange(this.selectedDate);
    const filter = `r_roomProfileToVenueBookings_c_roomProfileId eq '${roomProfileId}' and startTime gt ${startTime} and endTime lt ${endTime}`;
    this.availabilityCheckLoading = true;

    this.api.getBookedRoomProfiles(filter).subscribe({
      next: (response: any) => {
        const totalCount = Number(response?.totalCount ?? 0);
        if (totalCount > 0) {
          this.availabilityCheckLoading = false;
          this.sharedService.toastMessage('ERROR', 'Selected room is not available for this date', 'error');
          return;
        }
        this.reserveDraftRoomAndProceed(roomProfileId);
      },
      error: () => {
        this.availabilityCheckLoading = false;
      }
    });
  }

  // This method is used to reset
  reset() {
    this.selectedDate = this.selectedSlot = null;
    this.mode = 'slots';
  }

  // This method is used to date picked using date
  datePicked(date: any) {
    this.selectedDate = date;
  }

  onCalendarMonthChanged(monthDate: Date): void {
    this.calendarVisibleMonth = new Date(monthDate);
    this.loadBookedDatesForMonth(this.calendarVisibleMonth);
  }

  getSrDetail(): void {
    if (!this.srNumber) {
      return;
    }

    const userProfile = this.sharedService.getUserProfile() || {};
    const request = new VenueBookingQueryRequest();
    request.body.DCLoginName = String(userProfile?.['Login Name'] ?? '').trim();
    request.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();

    const sr = request.body.DCIncomingHierarchy['ListOfDC Community Portal SR IO']['DC Community Portal SR'];
    sr['SR Number'] = this.srNumber;
    sr['SR Type'] = ServiceName.VENUE_BOOKING;
    sr['Sub Type'] = ServiceName.VENUE_BOOKING;
    sr.CSN = this.userCSN;

    this.venueSrDetailLoading = true;
    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_QUERY_SERVICEREQUEST,
      request
    ).subscribe({
      next: (response: any) => {
        const srDetail = response?.SiebelMessage?.['DC Community Portal SR'];
        if (!srDetail) {
          return;
        }
        this.pendingVenueSrDetail = srDetail;
        this.currentSrStatus = String(srDetail?.['Status'] ?? '').trim();
        this.applySrDetailsToEventForm(srDetail);
        this.applySrDetailsToVenueSelection(srDetail);
        if (
          srDetail["Status"] != SrStatus.Draft &&
          srDetail["Status"] != SrStatus.AwaitingClarification &&
          srDetail["Status"] != SrStatus.Open
        ) {
          this.showActions = false;
        }
        // Booking date is past date then update status rejected and show message
        const isPastBookingDate = this.isBeforeTodayDateOnly(srDetail?.['DC Venue Booking End Time']);
        if ((srDetail["Status"] == SrStatus.Draft || srDetail["Status"] == SrStatus.ApprovedAndPaymentAwaited) && isPastBookingDate) {
          this.updateRequestStatus();
          this.sharedService.toastMessage('ERROR', 'VENUE_BOOKING_NOT_FOUND', 'error');
          this.router.navigate([`${RoutesPath.APPLICATIONS}`]);
          return;
        }
        this.loadVenueBookingClarificationDetails(srDetail);
        this.venueSrDetailLoading = false;
      },
      error: () => {
        this.venueSrDetailLoading = false;
      }
    });
  }

  private loadMasterData(): void {
    this.masterDataLoading = true;
    const userProfile = this.sharedService.getUserProfile();
    const userLoginName = userProfile?.['Login Name'] || '';
    const lang = this.sharedService.getCurrentLang() == 'ar' ? 'ARA' : 'ENU';

    const requestPayload = {
      body: {
        ProcessName: 'DC Venue Booking Get Master Data Profile WF',
        DCMethod: 'QUERY',
        DCLanguage: lang,
        DCLoginName: userLoginName,
        DCIncomingHierarchy: {
          MessageId: Utils.generateUniqueId(),
          IntObjectName: 'DC Venue Booking Profile IO',
          MessageType: 'Integration Object',
          IntObjectFormat: 'Siebel Hierarchical',
          'ListOfDC Venue Booking Profile IO': {
            'DC Venue Booking Profile BC': [
              {
                'SR Type': ServiceName.VENUE_BOOKING,
                'Sub Type': ServiceName.VENUE_BOOKING,
                'CSN': this.userCSN,
                'DC Channel': lang === 'ARA' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN
              }
            ]
          }
        }
      }
    };

    this.api.getVenueMasterData(requestPayload).subscribe({
      next: (response: any) => {
        this.masterDataLoading = false;
        const profile = Utils.checkIsArray(response?.SiebelMessage?.['DC Venue Booking Profile BC'])?.[0];
        if (!profile) {
          this.resetMasterDataOptions();
          if (this.srNumber) {
            this.getSrDetail();
          }
          return;
        }

        this.eventTypeOptions = this.mapMasterDataOptions(
          profile['DC Venue Booking EventType BC'],
          'DC EventType',
          'DC EventType Key'
        );
        this.roomSetupOptions = this.mapMasterDataOptions(
          profile['DC Venue Booking Setup BC'],
          'DC Setup Value',
          'DC Setup Key'
        );
        this.cateringOptionOptions = this.mapMasterDataOptions(
          profile['DC Venue Booking Catering Option BC'],
          'DC Catering Option',
          'DC Catering Option Key'
        );
        this.cateringTypeOptions = this.mapMasterDataOptions(
          profile['DC Venue Booking Catering Type BC'],
          'DC Catering Type',
          'DC Catering Type Key'
        );
        this.reminderOptions = this.mapMasterDataOptions(
          profile['DC Venue Booking Reminder BC'],
          'DC Reminder Value',
          'DC Reminder Key'
        );
        this.cancellationOptions = this.mapMasterDataOptions(
          profile['DC Venue Booking Cancel Reason BC'],
          'DC Cancel Reason',
          'DC Cancel Reason Key'
        );
        this.cateringProviderHotels = Utils.checkIsArray(
          profile['DC Venue Booking Catering Provider Hotel BC']
        )
          .slice()
          .sort((a: any, b: any) => Number(a?.['DC Order'] ?? 0) - Number(b?.['DC Order'] ?? 0));

        if (this.pendingVenueSrDetail) {
          this.applySrDetailsToEventForm(this.pendingVenueSrDetail);
        } else if (this.srNumber) {
          this.getSrDetail();
        }
      },
      error: () => {
        this.masterDataLoading = false;
        this.resetMasterDataOptions();
        if (this.srNumber) {
          this.getSrDetail();
        }
      }
    });
  }

  // This method is used to all room profiles
  private loadRoomProfiles(): void {
    const filter = "roomStatus eq 'available' and bookingOption ne 'internalEmployeeOnly' and isVenue eq true";
    this.roomProfilesLoading = true;

    this.api.getVenueRoomProfiles(filter).subscribe({
      next: (response: any) => {
        this.roomProfilesLoading = false;
        this.roomProfiles = Utils.checkIsArray(response?.items ?? response);
        if (this.roomProfiles.length && response?.totalCount > 0) {
          this.venues = this.mapRoomProfilesToVenues(this.roomProfiles);
        }
      },
      error: () => {
        this.roomProfilesLoading = false;
        this.roomProfiles = [];
      }
    });
  }

  private mapRoomProfilesToVenues(profiles: any[]): Venue[] {
    const staticVenues = Utils.checkIsArray(VenueData.list);
    return profiles.map((profile: any, index: number) => {
      const staticVenue = staticVenues[index % staticVenues.length] ?? {};
      const apiRoomImages = this.normalizeRoomImages(profile?.roomImages);
      const apiSliderImages = apiRoomImages
        .map((imgPath: string) => this.buildVenueImageUrl(imgPath))
        .filter((imgUrl: string) => !!imgUrl)
        .map((imgUrl: string) => ({ url: imgUrl }));

      const sliderImages = apiSliderImages.length
        ? apiSliderImages : [{ url: staticVenue?.image ?? '' }];

      const primaryImage = sliderImages[0]?.url || staticVenue?.image || '';
      const localizedRoomName = this.getLocalizedRoomName(profile?.roomTitleI18n);
      const localizedRoomDescription = this.getLocalizedRoomName(profile?.roomDescriptionI18n);

      const seatsValue = profile?.seatCapacity ?? '';
      const fullDayRateValue = profile?.priceOfRoom ?? 0;

      return {
        id: Number(profile?.id ?? profile?.roomProfileId ?? index + 1),
        roomProfileId: String(profile?.id ?? profile?.roomId ?? profile?.roomProfileId ?? profile?.c_roomProfileId ?? ''),
        name: String(localizedRoomName),
        desc: localizedRoomDescription ? localizedRoomDescription.replace(/<[^>]*>/g, '') : "",
        seats: String(seatsValue),
        capacity: Number(profile?.seatCapacity ?? profile?.capacity ?? 0),
        size: Number(profile?.size ? profile?.size : 0),
        amenities: this.mapAmenities(profile?.amenities, staticVenue?.amenities),
        amenityKeys: Utils.checkIsArray(profile?.amenities)
          .map((item: any) => String(item?.key ?? '').trim())
          .filter((x: string) => !!x),
        location: this.getLocationKey(profile?.location ?? profile?.venueLocation),
        locationName: profile?.location ? profile?.location.name : '',
        venueBookingId: Number(profile?.venueBookingId ?? 0),
        accessible: Boolean(profile?.accessible ?? profile?.isAccessible ?? staticVenue?.accessible ?? true),
        fullDayRate: Number(fullDayRateValue),
        hourlyRate: 0,
        memberRate: Number(fullDayRateValue),
        nonMemberRate: Number(fullDayRateValue),
        image: primaryImage,
        sliderImages,
        days: staticVenue?.days ?? [],
        productCode: profile.productCode
      };
    });
  }

  private normalizeRoomImages(roomImages: any): string[] {
    if (Array.isArray(roomImages)) {
      return roomImages
        .map((imagePath: any) => {
          let path = String(imagePath ?? '').trim();

          // Replace download=true with download=false
          path = path.replace(/download=true/g, 'download=false');

          return path;
        })
        .filter((imagePath: string) => !!imagePath);
    }

    if (typeof roomImages === 'string' && roomImages.trim()) {
      return [roomImages.trim().replace(/download=true/g, 'download=false')];
    }
    return [];
  }

  private buildVenueImageUrl(imagePath: string): string {
    const trimmedPath = String(imagePath ?? '').trim();
    if (!trimmedPath) {
      return '';
    }
    if (/^https?:\/\//i.test(trimmedPath)) {
      return trimmedPath;
    }
    const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
    return `${this.venueImageHostUrl}${normalizedPath}`;
  }

  truncateHtmlText(html: string, limit: number = 150): string {
    if (!html) return '';

    // Remove HTML tags safely
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const text = doc.body.textContent || '';

    // Trim text
    return text.length > limit
      ? text.substring(0, limit).trim() + '...'
      : text;
  }

  private proceedToConfirmation(): void {
    this.confirmedVenue = {
      venue: this.selectedVenue,
      day: this.selectedDate,
      slot: this.mode === 'slots' ? this.selectedSlot : 'full-day'
    };
    sessionStorage.setItem('selectedVenue', JSON.stringify(this.confirmedVenue));
    this.step = 2;
  }

  onEventFormProgressChange(progress: number): void {
    if (this.step !== 2) {
      return;
    }
    if (!this.allowVenueActions) {
      this.scheduleFormProgressUpdate(100);
      return;
    }
    const safeProgress = Number(progress);
    if (Number.isNaN(safeProgress)) {
      return;
    }
    this.scheduleFormProgressUpdate(Math.max(0, Math.min(100, safeProgress)));
  }

  private scheduleFormProgressUpdate(nextProgress: number): void {
    if (this.formProgressUpdateTimer) {
      clearTimeout(this.formProgressUpdateTimer);
    }
    this.formProgressUpdateTimer = setTimeout(() => {
      this.formProgress = nextProgress;
      this.formProgressUpdateTimer = null;
    }, 0);
  }

  onEventFormSubmit(formValue: any): void {
    if (this.currentSrStatus === SrStatus.AwaitingFinalPayment) {
      this.navigateToPaymentPage();
      return;
    }

    if (!this.selectedVenue || !this.selectedDate) {
      this.sharedService.toastMessage('ERROR', 'SELECT_DATE', 'error');
      return;
    }

    const roomProfileId = this.getRoomProfileId(this.selectedVenue);
    if (!roomProfileId) {
      this.sharedService.toastMessage('ERROR', 'V_PROFILEID_MISSING', 'error');
      return;
    }

    // If SR is in Awaiting Clarification status and customer has provided requested info, then directly call workflow to submit the info without reserving room again
    if (this.currentSrStatus == SrStatus.AwaitingClarification) {
      if (!formValue.requestedInfoByCustomer || !formValue.requestedInfoByCustomer.trim()) {
        this.sharedService.toastMessage('ERROR', 'V_PROVIDE_INFORMATION', 'error');
        return;
      }
      const venueBookingId = Number(
        this.selectedVenue?.venueBookingId ??
        this.confirmedVenue?.venue?.venueBookingId ??
        0
      );
      this.callProcessWorkflow('info', venueBookingId, formValue.requestedInfoByCustomer.trim());
    } else {
      if (this.currentSrStatus != SrStatus.AwaitingClarification) {
        this.reserveRoomWithForm(roomProfileId, formValue);
      }
    }
  }

  onSaveAndClose(): void {
    this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.VENUE.VENUE_LIST}`]);
  }

  onCancelBookingSubmit(event: { reason: string }): void {
    const venueBookingId = Number(
      this.selectedVenue?.venueBookingId ??
      this.confirmedVenue?.venue?.venueBookingId ??
      0
    );
    const reason = String(event?.reason ?? '').trim();

    if (!venueBookingId || Number.isNaN(venueBookingId) || !reason) {
      this.sharedService.toastMessage('ERROR', 'VALIDATION_MSG.FIELD_REQUIRED', 'error');
      return;
    }
    this.callProcessWorkflow('cancel', venueBookingId, reason);
  }

  callProcessWorkflow(type: string, venueBookingId: number, reason: string): void {
    const requestPayload = {
      classPK: venueBookingId,
      transitionName: type == 'cancel' ? 'cancelled-booking-from-provide-more-info' : 'information-provided',
      comment: reason
    };
    this.roomReservationLoading = true;
    this.api.processVenueBookingWorkflow(requestPayload).subscribe({
      next: (response: any) => {
        if (response) {
          this.updateVenueSrStatus(type == 'cancel' ? SrStatus.CancellationRequested : SrStatus.PendingNOC);
        }
      },
      error: () => {
        this.roomReservationLoading = false;
        this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
      }
    });
  }

  // This method is used to update SR Status in Siebel
  updateVenueSrStatus(status: string): void {
    let requestPayload =
    {
      "body": {
        "ProcessName": "DC Community Portal SR Process Wrapper WF",
        "DCMethod": "SR_STATUS_UPDATE",
        "DCLanguage": this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU',
        "DCIncomingHierarchy": {
          "MessageId": Utils.generateUniqueId(),
          "IntObjectName": "DC Community Portal SR Upsert IO",
          "MessageType": "Integration Object",
          "IntObjectFormat": "Siebel Hierarchical",
          "ListOfDC Community Portal SR Upsert IO": {
            "DC Community Portal SR":
            {
              "SR Number": this.srNumber,
              "Status": status
            }
          }
        }
      }
    }
    this.api.postWithoutRetry(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      requestPayload
    ).subscribe({
      next: (reseponse: any) => {
        if (reseponse['Error Code'] == null) {
          this.roomReservationLoading = false;
          if (status == SrStatus.CancellationRequested) {
            this.sharedService.toastMessage('SUCCESS', 'VENUE_BOOKING_CANCELLED', 'success');
          } else {
            this.sharedService.toastMessage('SUCCESS', 'VENUE_BOOKING_INFORMATION_SUBMITTED', 'success');
          }
          this.router.navigate([`${RoutesPath.DASHBOARD}`]);
        } else {
          this.roomReservationLoading = false;
          this.technicalErrorMsg = true;
        }
      },
      error: () => {
        this.roomReservationLoading = false;
      }
    });
  }

  private navigateToPaymentPage(): void {
    const nextSrNumber = String(this.srNumber ?? '').trim();
    if (nextSrNumber) {
      this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${nextSrNumber}`]);
      return;
    }

    this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}`]);
  }

  private reserveDraftRoomAndProceed(roomProfileId: string): void {
    const selectedVenue: any = this.selectedVenue ?? {};
    const { startTime, endTime } = this.getReservationRange(this.selectedDate, this.selectedSlot, this.mode);
    const requestPayload = {
      externalBooking: true,
      roomProfileId: Number(roomProfileId),
      startTime,
      endTime,
      scopeKey: 'Dubai Chamber Intranet',
      invitees: [],
      externalInvitees: [],
      venueBookingId: 0 //Number(selectedVenue?.venueBookingId ?? 0)
    };

    this.api.reserveVenueRoom(requestPayload).subscribe({
      next: (response: any) => {
        const validation = this.validateRoomBookResponse(response);
        if (!validation.isSuccess) {
          this.availabilityCheckLoading = false;
          this.sharedService.toastMessage('ERROR', 'ROOM_ALREADY_BOOKED', 'error');
          return;
        }

        this.availabilityCheckLoading = false;
        const returnedVenueBookingId = Number(
          response?.calenderBookingId ??
          0
        );
        if (returnedVenueBookingId > 0 && this.selectedVenue) {
          (this.selectedVenue as any).venueBookingId = returnedVenueBookingId;
        }
        sessionStorage.setItem('selectedVenue', JSON.stringify(this.confirmedVenue));
        this.submtEeventInformtionInSiebel({}, SrActionType.DRAFT);
        this.proceedToConfirmation();
      },
      error: () => {
        this.availabilityCheckLoading = false;
        this.sharedService.toastMessage('ERROR', 'ROOM_ALREADY_BOOKED', 'error');
      }
    });
  }

  private reserveRoomWithForm(roomProfileId: string, formValue: any): void {
    const selectedVenue: any = this.selectedVenue ?? {};
    const { startTime, endTime } = this.getReservationRange(this.selectedDate, this.selectedSlot, this.mode);
    const externalInvitees = this.parseExternalInvitees(formValue?.guestEmail);
    const requestPayload = {
      externalBooking: true,
      roomProfileId: Number(roomProfileId),
      title: formValue?.eventTitle || selectedVenue?.name || 'Room Booking',
      location: this.getLocationKey(selectedVenue?.location),
      amenities: Utils.checkIsArray(selectedVenue?.amenityKeys),
      eventType: formValue?.eventType,
      scopeKey: 'Dubai Chamber Intranet',
      totalNumberOfExternalAttendees: Number(formValue?.attendeeCount ?? 0),
      hasParking: Boolean(formValue?.needParking),
      totalNumberOfParking: Number(formValue?.parkingSlots ?? 0),
      startTime,
      endTime,
      description: formValue?.eventInfo || selectedVenue?.description || '',
      invitees: [],
      externalInvitees,
      setUp: formValue?.roomSetup,
      reminderBefore: formValue?.reminder || null,
      paymentSR: String(this.srNumber || '').trim(),
      accountHolderName: String(formValue?.accountHolderName ?? '').trim(),
      accountNumber: normalizeIban(formValue?.ibanNumber),
      bookingOption: 'internalEmployeesGeneralPublic',
      venueBookingId: Number(selectedVenue?.venueBookingId ?? 0),
      cateringTypes: formValue?.cateringType ? formValue.cateringType : [],
      cateringOption: formValue?.cateringOption //? this.getSelectedValueLabel(formValue?.cateringOption, 'cateringOption') : []
    };
    this.roomReservationLoading = true;
    this.api.reserveVenueRoom(
      requestPayload
    ).subscribe({
      next: (response: any) => {
        const validation = this.validateRoomBookResponse(response);
        if (!validation.isSuccess) {
          this.roomReservationLoading = false;
          this.sharedService.toastMessage('ERROR', 'ROOM_ALREADY_BOOKED', 'error');
          return;
        }
        this.submtEeventInformtionInSiebel(formValue, SrActionType.SUBMIT);
      },
      error: () => {
        this.roomReservationLoading = false;
        this.sharedService.toastMessage('ERROR', 'ROOM_ALREADY_BOOKED', 'error');
      }
    });
  }
  getSelectedValueLabel(value: string, optionKey: string) {
    let options: any = "";
    if (optionKey == 'eventType') {
      options = this.eventTypeOptions;
    }
    if (optionKey == 'cateringType') {
      options = this.cateringTypeOptions;
    }
    if (optionKey == 'cateringOption') {
      options = this.cateringOptionOptions;
    }
    if (optionKey == 'roomSetup') {
      options = this.roomSetupOptions;
    }
    if (optionKey == 'reminder') {
      options = this.reminderOptions;
    }
    const selectedOption = options.find((opt: any) => opt.value === value);
    return selectedOption ? selectedOption.label : '';
  }
  technicalErrorMsg: boolean = false;
  private submtEeventInformtionInSiebel(formValue: any, actionType: string): void {
    const selectedVenue: any = this.selectedVenue ?? {};
    this.technicalErrorMsg = false;
    const userProfile = this.sharedService.getUserProfile() || {};
    const requestPayload = new VenueBookingRequest();
    const roomProfileId = this.getRoomProfileId(selectedVenue);
    const { startTime, endTime } = this.getReservationRange(this.selectedDate, this.selectedSlot, this.mode);
    const attendeeCount = String(formValue?.attendeeCount ?? '0');
    const guestEmails = this.parseExternalInvitees(formValue?.guestEmail);
    const amenities = String(selectedVenue?.amenities ?? '')
      .split(',')
      .map((x: string) => x.trim())
      .filter((x: string) => !!x);

    requestPayload.body.DCLoginName = String(userProfile?.['Login Name'] ?? requestPayload.body.DCLoginName).trim();
    requestPayload.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';
    requestPayload.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    requestPayload.body.DCMethod = actionType == SrActionType.DRAFT ? 'SAVEASDRAFT' : 'SUBMIT';

    const sr = requestPayload.body.DCIncomingHierarchy['ListOfDC Community Portal SR Upsert IO']['DC Community Portal SR'];
    sr.CSN = this.userCSN ? this.userCSN : String(this.sharedService.getCsnAccountId('csn') ?? sr.CSN).trim();
    sr['SR Type'] = ServiceName.VENUE_BOOKING;
    sr['Sub Type'] = ServiceName.VENUE_BOOKING;
    sr['DC Payment Method'] = this.sharedService.getCurrentLang() === 'ar' ? FieldValue.PAYMENT_METHOD_ONLINE_AR : FieldValue.PAYMENT_METHOD_ONLINE_EN;
    sr['DC Channel'] = this.sharedService.getCurrentLang() === 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;
    sr['SR Number'] = this.srNumber && this.srNumber != null ? this.srNumber : '';
    sr['DC Venue Booking Title'] = String(formValue?.eventTitle ?? selectedVenue?.name ?? sr['DC Venue Booking Title']).trim();
    sr['DC Venue Booking Attendees'] = attendeeCount;
    sr['DC Venue Booking Catering Opt'] = this.getSelectedValueLabel(formValue?.cateringOption, 'cateringOption');
    sr['DC Venue Booking Start Time'] = this.toSiebelDateTime(startTime);
    sr['DC Venue Booking End Time'] = this.toSiebelDateTime(endTime);
    sr['DC Venue Booking Event Type'] = this.getSelectedValueLabel(formValue?.eventType, 'eventType');
    sr['DC Venue Booking Id'] = String(selectedVenue?.venueBookingId).trim();
    sr['DC Venue Booking Location'] = String(selectedVenue?.location ?? '').trim();
    sr['DC Venue Booking Loc Key'] = this.getLocationKey(selectedVenue?.location);
    sr['DC Venue Booking Notes'] = String(formValue?.eventInfo ?? '').trim();
    sr['DC Venue Booking Parking Req'] = formValue?.needParking && formValue?.needParking == 'Yes' ? 'Y' : 'N';
    sr['DC Venue Booking No Of Parking'] = String(formValue?.parkingSlots ?? "");
    sr['DC Venue Booking Reminder'] = this.getSelectedValueLabel(formValue?.reminder, 'reminder');
    sr['DC Venue Booking Room'] = String(selectedVenue?.name ?? '').trim();
    sr['DC Venue Booking RoomPrice'] = Math.round(
      Number(
        selectedVenue?.fullDayRate ??
        0
      )
    );
    sr['DC Venue Booking Room Prof Id'] = String(roomProfileId).trim();
    sr['DC Venue Booking Room Setup'] = this.getSelectedValueLabel(formValue?.roomSetup, 'roomSetup');
    sr['DC Venue Booking Email'] = String(formValue?.email ?? '').trim();
    sr['DC Account Holder'] = String(formValue?.accountHolderName ?? '').trim();
    sr['DC IBAN No'] = normalizeIban(formValue?.ibanNumber);
    sr['DC Venue Booking ProductCode'] = selectedVenue.productCode ?? "";

    let mobile = formValue?.mobileNumber ? `${formValue?.mobileCountryCode || '971'}${formValue?.mobileNumber}` : '';
    sr['DC Venue Booking Mobile'] = mobile;


    const guestList = (guestEmails.length ? guestEmails : [])
      .map((email: string) => ({
        Id: Utils.generateUniqueId(),
        'DC Guest Email Id': email
      }));
    sr['ListOfDC Venue Booking Guest BC']['DC Venue Booking Guest BC'] = (guestList.length ? guestList : '') as any;

    const amenityList = (amenities.length ? amenities : ['Screens', 'Sound System'])
      .map((name: string) => ({
        Id: Utils.generateUniqueId(),
        'DC Amenities': name
      }));
    sr['ListOfDC SR Venue Booking Amenities BC']['DC SR Venue Booking Amenities BC'] = (amenityList.length ? amenityList : '') as any;

    const cateringTypeList = (formValue?.cateringType && formValue?.cateringType?.length ? formValue?.cateringType : [])
      .map((type: string) => ({
        Id: Utils.generateUniqueId(),
        'DC Catering Type': this.getSelectedValueLabel(type, 'cateringType')
      }));
    sr['ListOfDC SR Venue Booking Catering Type BC']['DC SR Venue Booking Catering Type BC'] = (cateringTypeList.length ? cateringTypeList : '') as any;

    const roomImagesList: DCSRVenueBookingRoomImagesBC[] = Utils.checkIsArray(selectedVenue?.sliderImages)
      .map((img: any) => String(img?.url ?? '').trim())
      .filter((url: string) => !!url)
      .map((url: string) => ({
        Id: Utils.generateUniqueId(),
        'DC Room Image': url
      }));
    sr['ListOfDC SR Venue Booking Room Images BC'] = {
      'DC SR Venue Booking Room Images BC': (roomImagesList ? roomImagesList : '') as any
    };
    this.api.postWithoutRetry(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      requestPayload
    ).subscribe({
      next: (reseponse: any) => {
        if (reseponse['Error Code'] == null) {
          this.currentSrStatus = reseponse?.['DCSRStatus'];
          const nextSrNumber = reseponse.DCSRNumber;
          this.srNumber = nextSrNumber;
          sessionStorage.setItem('srNumber', nextSrNumber);

          if (actionType == SrActionType.DRAFT) {
            this.openFolder();
            this.updateBookingWithSrNumber(this.srNumber, roomProfileId);
          }
          if (actionType != SrActionType.DRAFT) {
            this.roomReservationLoading = false;
            this.sharedService.toastMessage('SUCCESS', 'SUCCESS', 'success');
            this.navigateToPaymentPage();
          }
        } else {
          this.roomReservationLoading = false;
          this.technicalErrorMsg = true;
        }
      },
      error: () => {
        this.roomReservationLoading = false;
      }
    });
  }

  updateBookingWithSrNumber(srNumber: string, roomProfileId: string): void {
    const { startTime, endTime } = this.getReservationRange(this.selectedDate, this.selectedSlot, this.mode);
    const venueBookingId = String(this.selectedVenue?.venueBookingId ?? '').trim();
    const requestPayload = {
      externalBooking: true,
      roomProfileId: Number(roomProfileId),
      startTime,
      endTime,
      scopeKey: 'Dubai Chamber Intranet',
      invitees: [],
      externalInvitees: [],
      venueBookingId: venueBookingId,
      paymentSR: srNumber.trim(),
    };

    this.api.reserveVenueRoom(requestPayload).subscribe({
      next: (response: any) => {
        const validation = this.validateRoomBookResponse(response);
      }
    });
  }

  private toSiebelDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const yyyy = String(date.getUTCFullYear());
    const hh = String(date.getUTCHours()).padStart(2, '0');
    const mi = String(date.getUTCMinutes()).padStart(2, '0');
    const ss = String(date.getUTCSeconds()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  private getRoomProfileId(venue: any): string {
    return String(venue?.roomProfileId ?? venue?.id ?? '').trim();
  }

  // This method is used to load booked dates for the selected room profile and month
  private loadBookedDatesForMonth(monthDate: Date): void {
    const selectedVenue: any = this.selectedVenue ?? {};
    const roomProfileId = this.getRoomProfileId(selectedVenue);
    if (!roomProfileId) {
      this.bookedDates = [];
      return;
    }

    const { startTime, endTime } = this.getMonthAvailabilityRange(monthDate);
    const filter = `r_roomProfileToVenueBookings_c_roomProfileId eq '${roomProfileId}' and startTime gt ${startTime} and endTime lt ${endTime}`;

    this.api.getBookedRoomProfiles(filter).subscribe({
      next: (response: any) => {
        const totalCount = Number(response?.totalCount ?? 0);
        if (totalCount <= 0) {
          this.bookedDates = [];
          return;
        }

        this.bookedDates = Array.from(new Set(
          Utils.checkIsArray(response?.items)
            .map((item: any) => this.toDateKey(item?.startTime))
            .filter((dateKey: string) => !!dateKey)
        ));
      },
      error: () => {
        this.bookedDates = [];
      }
    });
  }

  private getMonthAvailabilityRange(date: Date): { startTime: string; endTime: string } {
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    return {
      startTime: `${this.toDateKey(monthStart)}T00:00:00.000Z`,
      endTime: `${this.toDateKey(monthEnd)}T23:59:59.999Z`
    };
  }

  private toDateKey(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getAvailabilityRange(date: any): { startTime: string; endTime: string } {
    const selected = new Date(date);
    const year = selected.getFullYear();
    const month = String(selected.getMonth() + 1).padStart(2, '0');
    const day = String(selected.getDate()).padStart(2, '0');
    const baseDate = `${year}-${month}-${day}`;

    return {
      startTime: `${baseDate}T08:00:00.000Z`,
      endTime: `${baseDate}T19:00:00.000Z`
    };
  }

  private getReservationRange(date: any, slot: TimeSlot | null, mode: 'slots' | 'full'): { startTime: string; endTime: string } {
    const selected = new Date(date);
    const year = selected.getFullYear();
    const month = String(selected.getMonth() + 1).padStart(2, '0');
    const day = String(selected.getDate()).padStart(2, '0');
    const baseDate = `${year}-${month}-${day}`;

    if (mode === 'slots' && slot?.label) {
      const match = String(slot.label).match(/(\d{2}:\d{2})\s*[-–]\s*(\d{2}:\d{2})/);
      if (match) {
        return {
          startTime: `${baseDate}T${match[1]}:00.000Z`,
          endTime: `${baseDate}T${match[2]}:00.000Z`
        };
      }
    }

    return {
      startTime: `${baseDate}T08:00:00.000Z`,
      endTime: `${baseDate}T19:00:00.000Z`
    };
  }

  private parseExternalInvitees(guestEmail: any): string[] {
    if (!guestEmail) {
      return [];
    }
    if (Array.isArray(guestEmail)) {
      return guestEmail.filter((x: any) => !!x).map((x: any) => String(x).trim());
    }
    return String(guestEmail)
      .split(',')
      .map((x: string) => x.trim())
      .filter((x: string) => !!x);
  }

  private getLocationKey(location: any): string {
    if (!location) {
      return '';
    }
    if (typeof location === 'string') {
      return location.trim();
    }
    return String(location?.key ?? '').trim();
  }

  private validateRoomBookResponse(response: any): { isSuccess: boolean; message: string } {
    const calendarBookingId = response?.calenderBookingId ?? response?.calendarBookingId;
    const roomProfileId = response?.roomProfileId;
    const isSuccess = !!calendarBookingId && !!roomProfileId;
    const message = response?.title || 'Unable to reserve room';
    return { isSuccess, message };
  }

  private mapAmenities(amenities: any, fallbackAmenities: any): string {
    const amenityList = Utils.checkIsArray(amenities);
    if (amenityList.length) {
      return amenityList
        .map((item: any) => String(item?.name ?? item?.label ?? item?.key ?? '').trim())
        .filter((name: string) => !!name)
        .join(', ');
    }

    return String(fallbackAmenities ?? '');
  }

  private getLocalizedRoomName(roomTitleI18n: any): string {
    if (!roomTitleI18n) {
      return '';
    }

    try {
      const parsed = typeof roomTitleI18n === 'string' ? JSON.parse(roomTitleI18n) : roomTitleI18n;
      const isArabic = this.sharedService.getCurrentLang() === 'ar';
      return String(parsed?.[isArabic ? 'ar_SA' : 'en_US'] ?? parsed?.en_US ?? parsed?.ar_SA ?? '').trim();
    } catch {
      return '';
    }
  }

  private mapMasterDataOptions(list: any, labelKey: string, valueKey: string): Array<{ label: string; value: string }> {
    const items = Utils.checkIsArray(list);
    return items
      .slice()
      .sort((a: any, b: any) => Number(a?.['DC Order'] ?? 0) - Number(b?.['DC Order'] ?? 0))
      .map((item: any) => ({
        label: String(item?.[labelKey] ?? '').trim(),
        value: String(item?.[valueKey] ?? item?.[labelKey] ?? '').trim()
      }))
      .filter((item: any) => item.label);
  }

  private resetMasterDataOptions(): void {
    this.eventTypeOptions = [];
    this.roomSetupOptions = [];
    this.cateringOptionOptions = [];
    this.cateringTypeOptions = [];
    this.reminderOptions = [];
    this.cateringProviderHotels = [];
  }

  // This method is triggered when a user clicks on a catering provider item in the UI. It checks if the clicked item is related to clicking or catering, and if so, it initiates the download of a PDF containing the catering providers' information. This allows users to easily access and save the contact details of catering providers for their venue booking needs.  
  onValidationItemClick(item: string): void {
    const normalizedItem = String(item ?? '').toLowerCase();
    const isClickLabel = normalizedItem.includes('here') || normalizedItem.includes('click') || normalizedItem.includes('التموين من خلال');
    if (normalizedItem && !isClickLabel) {
      return;
    }
    void this.downloadCateringProvidersPdfFromHtml();
  }

  private async downloadCateringProvidersPdfFromHtml(): Promise<void> {
    if (this.cateringProviderPdfLoading) {
      return;
    }

    this.cateringProviderPdfLoading = true;
    try {
      const htmlResponse = await this.getCateringProviderHtmlForPdf();
      await generateCateringProvidersPdfFromHtml(htmlResponse);
    } catch (error) {
      this.sharedService.toastMessage('ERROR', 'TECHNICAL_ERROR', 'error');
    } finally {
      this.cateringProviderPdfLoading = false;
    }
  }


  private async getCateringProviderHtmlForPdf(): Promise<string> {
    return firstValueFrom(this.api.getVenueCateringProviderTable());
  }


  ngOnDestroy(): void {
    if (this.formProgressUpdateTimer) {
      clearTimeout(this.formProgressUpdateTimer);
      this.formProgressUpdateTimer = null;
    }
    if (sessionStorage.getItem('selectedVenue')) {
      sessionStorage.removeItem('selectedVenue');
    }
  }

  private applySrDetailsToVenueSelection(srDetail: any): void {
    const roomProfileId = String(srDetail?.['DC Venue Booking Room Prof Id'] ?? '').trim();
    const roomName = String(srDetail?.['DC Venue Booking Room'] ?? '').trim().toLowerCase();
    const selectedStartDate = this.parseSiebelDateTime(srDetail?.['DC Venue Booking Start Time']);
    const srRoomImageUrls = this.extractSrRoomImageUrls(srDetail);

    let matchedVenue: any = this.venues?.find((venue: any) => {
      const venueRoomProfileId = this.getRoomProfileId(venue);
      if (roomProfileId && venueRoomProfileId === roomProfileId) {
        return true;
      }
      return roomName && String(venue?.name ?? '').trim().toLowerCase() === roomName;
    });
    if (matchedVenue) {
      matchedVenue['venueBookingId'] = String(srDetail?.['DC Venue Booking Id'] ?? '').trim();
    }

    const fallbackVenue = this.createFallbackVenueFromSr(srDetail);
    const selectedVenue = (matchedVenue ?? fallbackVenue) as any;
    // const selectedVenue =  fallbackVenue as any;
    if (srRoomImageUrls.length) {
      selectedVenue.sliderImages = srRoomImageUrls.map((url: string) => ({ url }));
      selectedVenue.image = selectedVenue.sliderImages[0]?.url || selectedVenue.image || '';
    }

    this.selectedVenue = selectedVenue;
    this.selectedDate = selectedStartDate ?? new Date();
    this.confirmedVenue = {
      venue: this.selectedVenue,
      day: this.selectedDate,
      slot: 'full-day'
    };
    this.step = 2;
  }

  private applySrDetailsToEventForm(srDetail: any): void {
    const guestEmailList = Utils.checkIsArray(srDetail?.['DC Venue Booking Guest BC'])
      .map((item: any) => String(item?.['DC Guest Email Id'] ?? '').trim())
      .filter((email: string) => !!email);

    const cateringTypeList = Utils.checkIsArray(srDetail?.['DC SR Venue Booking Catering Type BC'])
      .map((item: any) => String(item?.['DC Catering Type LIC'] ?? item?.['DC Catering Type'] ?? '').trim())
      .filter((type: string) => !!type);

    const mobileRaw = String(srDetail?.['DC Venue Booking Mobile'] ?? srDetail?.['DC Created User Phone Number'] ?? '').trim();
    const mobileCountryCode = mobileRaw ? Utils.parseMobile(mobileRaw, 'cc') : '';
    const mobileNumber = mobileRaw ? Utils.parseMobile(mobileRaw, 'number') : '';

    this.eventFormInitialData = {
      organiserName: String(srDetail?.['DC Member Name ENU'] ?? '').trim(),
      organiserNameAr: String(srDetail?.['DC Member Name ARA'] ?? '').trim(),
      eventTitle: String(srDetail?.['DC Venue Booking Title'] ?? '').trim(),
      email: String(srDetail?.['DC Venue Booking Email'] ?? srDetail?.['DC Created User Email'] ?? '').trim(),
      mobileCountryCode: mobileCountryCode || '971',
      mobileNumber: mobileNumber || '',
      eventType: this.getOptionValueByLabelOrValue(this.eventTypeOptions, srDetail?.['DC Venue Booking Event Type']),
      roomSetup: this.getOptionValueByLabelOrValue(this.roomSetupOptions, srDetail?.['DC Venue Booking Room Setup']),
      cateringOption: this.getOptionValueByLabelOrValue(this.cateringOptionOptions, srDetail?.['DC Venue Booking Catering Opt']),
      cateringType: cateringTypeList.map((type: string) => this.getOptionValueByLabelOrValue(this.cateringTypeOptions, type)).filter((type: string) => !!type),
      reminder: this.getOptionValueByLabelOrValue(this.reminderOptions, srDetail?.['DC Venue Booking Reminder']),
      eventInfo: String(srDetail?.['DC Venue Booking Notes'] ?? '').trim(),
      attendeeCount: String(srDetail?.['DC Venue Booking Attendees'] ?? '').trim(),
      guestEmail: '',
      guestEmails: guestEmailList,
      needParking: srDetail?.['DC Venue Booking Parking Req'] == 'Y' ? 'Yes' : 'No',
      parkingSlots: String(srDetail?.['DC Venue Booking No Of Parking'] ?? '').trim(),
      accountHolderName: String(srDetail?.['DC Account Holder'] ?? '').trim(),
      ibanNumber: normalizeIban(srDetail?.['DC IBAN No']),
      requestedInfoByAdmin: String(srDetail?.['DC Requested Info By Admin'] ?? '').trim(),
      requestedInfoByCustomer: String(srDetail?.['DC Requested Info By Customer'] ?? '').trim(),
      confirmInformation: true
    };
  }

  private extractSrRoomImageUrls(srDetail: any): string[] {
    const directList = Utils.checkIsArray(srDetail?.['DC SR Venue Booking Room Images BC']);
    const nestedList = Utils.checkIsArray(
      srDetail?.['ListOfDC SR Venue Booking Room Images BC']?.['DC SR Venue Booking Room Images BC']
    );

    const source = directList.length ? directList : nestedList;
    return source
      .map((item: any) => String(item?.['DC Room Image'] ?? '').trim())
      .filter((url: string) => !!url);
  }

  private getOptionValueByLabelOrValue(
    options: Array<{ label: string; value: string }>,
    rawValue: any
  ): string {
    const candidate = String(rawValue ?? '').trim();
    if (!candidate) {
      return '';
    }

    const normalized = candidate.toLowerCase();
    const matched = options.find((option) => {
      const optionValue = String(option?.value ?? '').trim().toLowerCase();
      const optionLabel = String(option?.label ?? '').trim().toLowerCase();
      return optionValue === normalized || optionLabel === normalized;
    });

    return matched?.value ?? candidate;
  }

  private parseSiebelDateTime(value: any): Date | null {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return null;
    }

    const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
      const fallbackDate = new Date(raw);
      return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
    }

    const month = Number(match[1]) - 1;
    const day = Number(match[2]);
    const year = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);
    const seconds = Number(match[6]);

    const parsed = new Date(year, month, day, hours, minutes, seconds);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private createFallbackVenueFromSr(srDetail: any): Venue {
    let amenityList =
      srDetail?.['DC SR Venue Booking Amenities BC']?.map((item: any) => String(item?.['DC Amenities'] ?? '').trim())
        .filter((name: string) => !!name)
        .join(', ');
    return {
      id: Number(srDetail?.['DC Venue Booking Room Prof Id'] ?? 0) || 0,
      name: String(srDetail?.['DC Venue Booking Room'] ?? '').trim(),
      desc: '',
      seats: String(srDetail?.['DC Venue Booking Attendees'] ?? '0').trim(),
      accessible: false,
      fullDayRate: 0,
      hourlyRate: 0,
      image: '',
      days: [],
      venueBookingId: Number(srDetail?.['DC Venue Booking Id'] ?? 0) || 0,
      productCode: srDetail?.['DC Venue Booking ProductCode'],
      amenities: amenityList ? amenityList : ''
    } as Venue;
  }

  // This method is used to open folder
  srFolder: any = null;
  mainFromSectionDocs: any = [];
  openFolder() {
    let documentType = DocumentServiceType.VENB;
    let isLabels = !this.srNumber;

    this.sharedService.getDocuments(this.srNumber, documentType, isLabels)
      .subscribe({

        next: (response: any) => {
          if (response.success) {
            // If SR exists → response.data, otherwise → response
            this.srFolder = response.data ? response.data : response;
            this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes?.filter((d: any) => !d.forUISection);

          } else {
            this.sharedService.toastMessage("error", response.message, 'error');
          }
        },

        error: err => { }
      });
  }


  private loadVenueBookingClarificationDetails(srDetail: any): void {
    const venueBookingId = String(srDetail?.['DC Venue Booking Id'] ?? '').trim();
    if (!venueBookingId) {
      return;
    }

    this.api.getVenueBookingDetail<any>(venueBookingId).subscribe({
      next: (bookingDetail: any) => {
        if (bookingDetail?.status == 'NOT_FOUND' && (srDetail.Status == SrStatus.Draft || srDetail.Status == SrStatus.ApprovedAndPaymentAwaited)) {
          this.updateRequestStatus();
          this.sharedService.toastMessage('ERROR', 'VENUE_BOOKING_NOT_FOUND', 'error');
          this.router.navigate([`${RoutesPath.APPLICATIONS}`]);
          return;
        } else {
          this.eventFormInitialData = {
            ...(this.eventFormInitialData ?? {}),
            requestedInfoByAdmin: String(bookingDetail?.requestMoreInfoByAdmin ?? '').trim(),
            requestedInfoByCustomer: String(bookingDetail?.requestMoreInfoByExternalUser ?? '').trim(),
            reasonForCancellation: String(bookingDetail?.reasonForCancellation ?? '').trim(),
            rejectionReason: String(bookingDetail?.rejectionReason ?? '').trim()
          };
        }
      }
    });
  }

  private isBeforeTodayDateOnly(value: any): boolean {
    const bookingDate = this.parseSiebelDateTime(value);
    if (!bookingDate) {
      return false;
    }

    const bookingDay = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
    const today = new Date();
    const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return bookingDay < todayDay;
  }

  // This method is use to update sr status as rejected in siebel when the booking is not found in the venue booking system
  private updateRequestStatus(): void {
    const userProfile = this.sharedService.getUserProfile() || {};
    const requestPayload = new SrStatusRequest();
    requestPayload.body.DCLoginName = String(userProfile?.['Login Name'] ?? '').trim();
    requestPayload.body.DCLanguage = this.sharedService.getCurrentLang() === 'ar' ? 'ARA' : 'ENU';
    requestPayload.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();

    const sr = requestPayload.body.DCIncomingHierarchy['ListOfDC Community Portal SR Upsert IO']['DC Community Portal SR'];
    sr['SR Number'] = this.srNumber;
    sr.Status = SrStatus.Rejected;

    this.api.postWithoutRetry(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      requestPayload
    ).subscribe({
      next: (response: any) => {
        if (response?.['Error Code'] == null) {
          this.currentSrStatus = SrStatus.Rejected;
        }
      }
    });
  }

}

interface VenueCateringProviderHotel {
  [key: string]: any;
}

